import React, { useMemo, useState } from 'react';
import transformPoll from 'utils/transformPoll';
import addPercentageToArchiveData from './addPercentageToArchiveData';
import {
  ArchivePollDaysResponse,
  CountingCirclesResponse,
  IssueDownloadsResponse,
  OverviewPollDay,
  PollDayDownloadsResponse,
  PollDayResponse,
  PollDaysResponse,
  PollResponse,
} from './api.types';
import getFetchData from './getFetchData';
import { getOpendataOverview, getOpendataPollDay } from './opendataApi';
import { OpendataOverview, OpendataPollDay } from './opendataApi.types';
import transformArchiveData from './transformArchiveData';
import sortPollDay from './sortPollDay';

type Language = 'de' | 'fr' | 'it' | 'rm';
export type DownloadsType = 'polling_days' | 'issues';

const ApiContext = React.createContext<{
  apiUrl: string;
  archiveApiUrl: string;
  archiveDownloadsUrl: string;
  mapUrl: string;
  language: Language;
  getPollDays: () => Promise<PollDaysResponse>;
  getPollDay: (id: string) => Promise<PollDayResponse>;
  getPoll: (id: string, isPrevResult?: boolean) => Promise<PollResponse>;
  getDownloads: (
    id: string,
    type: DownloadsType,
  ) => Promise<PollDayDownloadsResponse | IssueDownloadsResponse>;
  getOpendataOverview: () => Promise<OpendataOverview>;
  getOpendataPollDay: (url: string) => Promise<OpendataPollDay>;
  getCountingCircles: (id: string) => Promise<CountingCirclesResponse>;
  getAggregatingDomains: (id: string) => Promise<CountingCirclesResponse>;
}>(null);

interface Props {
  children: React.ReactNode;
  apiUrl: string;
  archiveApiUrl: string;
  mapUrl: string;
  previewUser?: string;
  previewPassword?: string;
}

// archive ids always have this format: YYYYMMDD[1-9]* e.g. 201705241
// and the date is always before 01.01.2018
export const isArchiveId = (id: string) => {
  const match = String(id).match(/(\d{4})(\d{2})(\d{2})/);
  if (!match) return false;

  const [year, month, day] = match.slice(1);
  const JAN_1_2018 = 1514764800000;
  return new Date(`${year}-${month}-${day}`).getTime() < JAN_1_2018;
};

export const ApiContextProvider = ({
  children,
  apiUrl,
  archiveApiUrl,
  mapUrl,
  previewUser,
  previewPassword,
}: Props) => {
  const [language] = useState<Language>('de');
  const fetchData = getFetchData(apiUrl, previewUser, previewPassword, true);
  const fetchArchiveData = getFetchData(archiveApiUrl);

  const archiveDownloadsUrl = archiveApiUrl.replace('/data', '/downloads');

  const value = useMemo(() => {
    const getPollDays = async () => {
      const apiData = await fetchData<PollDaysResponse>('/polling_days.json');
      const archiveData = await fetchArchiveData<ArchivePollDaysResponse>(
        '/polling_days.json',
      );
      const transformedArchiveData = transformArchiveData(archiveData);

      const pollDays = [...apiData.data, ...transformedArchiveData.data];
      const pollDaysWithSortedIssues: OverviewPollDay[] = pollDays.map((pollDay) => {
        // transform to type Issue[], so sortPollDay will accept it
        const sortedIssues = sortPollDay(
          pollDay.issues.map((issue) => ({
            info: issue,
            progress: null,
            candidates: null,
            lists: null,
            list_groups: null,
            results: null,
          })),
        ).map((issue) => issue.info);
        return {
          ...pollDay,
          issues: sortedIssues,
        };
      });
      return {
        data: pollDaysWithSortedIssues,
      };
    };

    const getPollDay = async (issueId: string) => {
      const path = `polling_days/${issueId}.json`;
      if (!isArchiveId(issueId)) {
        const response = await fetchData<PollDayResponse>(path);
        const transformedData = addPercentageToArchiveData<PollDayResponse>(response);
        const sortedIssues = sortPollDay(transformedData.data.issues);
        const responseWithSortedIssues = {
          ...transformedData,
          data: {
            ...transformedData.data,
            issues: sortedIssues,
          },
        };
        return responseWithSortedIssues;
      }

      const response = await fetchArchiveData<PollDayResponse>(path);
      const transformedData = addPercentageToArchiveData<PollDayResponse>(response);
      const sortedIssues = sortPollDay(transformedData.data.issues);
      const responseWithSortedIssues = {
        ...transformedData,
        data: {
          ...transformedData.data,
          issues: sortedIssues,
        },
      };

      return responseWithSortedIssues;
    };

    const getPoll = async (issueId: string, isPrevResult = false) => {
      const path = `issues/${issueId}.json`;
      if (!isArchiveId(issueId)) {
        const response = await fetchData<PollResponse>(path);
        return transformPoll(response, isPrevResult, apiUrl);
      }
      const response = await fetchArchiveData<PollResponse>(path);
      return transformPoll(
        addPercentageToArchiveData<PollResponse>(response),
        isPrevResult,
        apiUrl,
      );
    };

    const getDownloads = (id: string, type: DownloadsType) => {
      const fetchFunction = isArchiveId(id) ? fetchArchiveData : fetchData;
      const path = `${type}/${id}/downloads.json`;
      return fetchFunction<PollDayDownloadsResponse | IssueDownloadsResponse>(path);
    };

    const getCountingCircles = (issueId: string) => {
      const path = `/issues/${issueId}/counting_circles.json`;
      if (!isArchiveId(issueId)) {
        return fetchData<CountingCirclesResponse>(path);
      }
      return fetchArchiveData<CountingCirclesResponse>(path);
    };

    const getAggregatingDomains = (issueId: string) => {
      if (!issueId) return undefined;
      const path = `/issues/${issueId}/aggregating_domains.json`;
      if (!isArchiveId(issueId)) {
        return fetchData<CountingCirclesResponse>(path);
      }
      return fetchArchiveData<CountingCirclesResponse>(path);
    };

    return {
      apiUrl,
      archiveApiUrl,
      archiveDownloadsUrl,
      mapUrl,
      language,
      getPollDays,
      getPollDay,
      getPoll,
      getDownloads,
      getOpendataOverview,
      getOpendataPollDay,
      getCountingCircles,
      getAggregatingDomains,
    };
  }, [apiUrl, archiveApiUrl, previewUser, previewPassword]);

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

export default ApiContext;
